import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { createApiClient, buildBackendUrl, clearStoredMenus as clearCoreMenuCache } from '@textile-erp/core';
import { useAuthContext } from './AuthProvider';

// Create API client for menu operations
const apiClient = createApiClient(buildBackendUrl('/api/identity'));

// Types
interface MenuTreeNode {
  id: string;
  name: string;
  displayName: string;
  path: string | null;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  parentId: string | null;
  children: MenuTreeNode[];
}

// Scopes type: { menuName: ['scope1', 'scope2', ...] }
type UserScopes = Record<string, string[]>;

// Path to menu name mapping type
type PathToMenuName = Record<string, string>;

// Path to menu ID mapping type
type PathToMenuId = Record<string, string>;

interface UserMenusData {
  menus: MenuTreeNode[];
  paths: string[];
  scopes: UserScopes;
  pathToMenuName: PathToMenuName;
  pathToMenuId: PathToMenuId;
  isAdmin: boolean;
  fetchedAt: string;
}

interface MenuContextValue {
  userMenus: MenuTreeNode[];
  accessiblePaths: string[];
  userScopes: UserScopes;
  pathToMenuId: PathToMenuId;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  hasAccess: (path: string) => boolean;
  hasModuleAccess: (modulePath: string) => boolean;
  hasScope: (menuName: string, scopeName: string) => boolean;
  getMenuScopes: (menuName: string) => string[];
  hasScopesForPath: (path: string) => boolean;
  refreshMenus: (opts?: { silent?: boolean }) => Promise<void>;
  clearMenus: () => void;
}

const STORAGE_KEY = 'textile_erp_user_menus';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

const MenuContext = createContext<MenuContextValue | undefined>(undefined);

export function MenuProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuthContext();
  const [userMenus, setUserMenus] = useState<MenuTreeNode[]>([]);
  const [accessiblePaths, setAccessiblePaths] = useState<string[]>([]);
  const [userScopes, setUserScopes] = useState<UserScopes>({});
  const [pathToMenuName, setPathToMenuName] = useState<PathToMenuName>({});
  const [pathToMenuId, setPathToMenuId] = useState<PathToMenuId>({});
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load cached menus from localStorage
  const loadCachedMenus = useCallback((): UserMenusData | null => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (!cached) return null;

      const data: UserMenusData = JSON.parse(cached);

      // Check if cache is expired
      const fetchedAt = new Date(data.fetchedAt).getTime();
      const now = Date.now();
      if (now - fetchedAt > CACHE_DURATION_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Failed to load cached menus:', err);
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  // Save menus to localStorage
  const saveMenusToCache = useCallback((menus: MenuTreeNode[], paths: string[], scopes: UserScopes, pathToMenuNameMap: PathToMenuName, pathToMenuIdMap: PathToMenuId, adminFlag: boolean) => {
    try {
      const data: UserMenusData = {
        menus,
        paths,
        scopes,
        pathToMenuName: pathToMenuNameMap,
        pathToMenuId: pathToMenuIdMap,
        isAdmin: adminFlag,
        fetchedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
      console.error('Failed to cache menus:', err);
    }
  }, []);

  // Fetch menus from API
  const fetchMenusFromAPI = useCallback(async (): Promise<{ menus: MenuTreeNode[]; paths: string[]; scopes: UserScopes; pathToMenuName: PathToMenuName; pathToMenuId: PathToMenuId; isAdmin: boolean } | null> => {
    try {
      // Ensure token is set for API client
      const token = localStorage.getItem('textile_erp_token');
      if (token) {
        apiClient.setAuthToken(token);
      }

      const response = await apiClient.get('/users/me/menus') as any;
      return {
        menus: response.menus || [],
        paths: response.paths || [],
        scopes: response.scopes || {},
        pathToMenuName: response.pathToMenuName || {},
        pathToMenuId: response.pathToMenuId || {},
        isAdmin: response.isAdmin || false,
      };
    } catch (err: any) {
      console.error('Failed to fetch menus from API:', err);
      throw err;
    }
  }, []);

  // Main function to refresh menus.
  // `opts.silent` = background refresh (the periodic 5-min keep-up-to-date tick).
  // A silent refresh must NEVER toggle `loading` (it gates page rendering via
  // ProtectedRoute) and must NEVER wipe accessiblePaths on a transient error —
  // otherwise the user gets bounced out of the form they're working on and sent
  // to the first accessible/list page.
  const refreshMenus = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;

    if (!isAuthenticated || !user) {
      setUserMenus([]);
      setAccessiblePaths([]);
      setUserScopes({});
      setPathToMenuName({});
      setPathToMenuId({});
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      // Always fetch fresh data from API (cache disabled for accurate scope checking)
      // Clear any stale cache first (both MenuProvider's and core service's caches)
      localStorage.removeItem(STORAGE_KEY);
      clearCoreMenuCache();

      // Fetch from API
      const result = await fetchMenusFromAPI();
      if (result) {
        setUserMenus(result.menus);
        setAccessiblePaths(result.paths);
        setUserScopes(result.scopes || {});
        setPathToMenuName(result.pathToMenuName || {});
        setPathToMenuId(result.pathToMenuId || {});
        setIsAdmin(result.isAdmin || false);
        saveMenusToCache(result.menus, result.paths, result.scopes || {}, result.pathToMenuName || {}, result.pathToMenuId || {}, result.isAdmin || false);
      }
    } catch (err: any) {
      if (silent) {
        // Background refresh failed — keep the last-known-good menus/paths so the
        // user is NOT redirected away from their current page. Just log it.
        console.warn('[MenuProvider] Silent menu refresh failed, keeping cached access:', err?.message);
      } else {
        setError(err.message || 'Failed to load menu permissions');
        setUserMenus([]);
        setAccessiblePaths([]);
        setUserScopes({});
        setPathToMenuName({});
        setPathToMenuId({});
        setIsAdmin(false);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAuthenticated, user, loadCachedMenus, fetchMenusFromAPI, saveMenusToCache]);

  // Clear menus (on logout)
  const clearMenus = useCallback(() => {
    setUserMenus([]);
    setAccessiblePaths([]);
    setUserScopes({});
    setPathToMenuName({});
    setPathToMenuId({});
    setIsAdmin(false);
    setLoading(false);
    setError(null);
    localStorage.removeItem(STORAGE_KEY);
    clearCoreMenuCache();
  }, []);

  // Check if user has access to a specific path
  const hasAccess = useCallback((path: string): boolean => {
    // Always allow dashboard access
    if (path === '/' || path === '/dashboard') {
      return true;
    }

    // Check exact path match
    if (accessiblePaths.includes(path)) {
      return true;
    }

    // Check if path is a sub-path of any accessible path
    // e.g., /parking/vehicle-types/create should be allowed if /parking/vehicle-types is accessible
    for (const accessiblePath of accessiblePaths) {
      if (path.startsWith(accessiblePath + '/')) {
        return true;
      }
    }

    // Check if path is a PARENT of any accessible path (module landing pages)
    // e.g., /parking should be allowed if /parking/check-ins is accessible
    // This only applies to module root paths (e.g., /parking)
    // so the user can navigate to the module landing page
    const pathParts = path.split('/').filter(Boolean);
    if (pathParts.length === 1) {
      const modulePath = '/' + pathParts[0];
      for (const accessiblePath of accessiblePaths) {
        if (accessiblePath.startsWith(modulePath + '/')) {
          return true;
        }
      }
    }

    return false;
  }, [accessiblePaths]);

  // Check if user has access to a module (top-level path)
  const hasModuleAccess = useCallback((modulePath: string): boolean => {
    // Always allow dashboard
    if (modulePath === '/' || modulePath === '/dashboard') {
      return true;
    }

    // Check if user has any accessible path under this module
    const result = accessiblePaths.some(path => path === modulePath || path.startsWith(modulePath + '/'));

    // Debug logging

    return result;
  }, [accessiblePaths]);

  // Check if user has a specific scope for a menu
  const hasScope = useCallback((menuName: string, scopeName: string): boolean => {
    // Super Admin / system role users have all scopes
    if (isAdmin) {
      return true;
    }

    const menuScopeList = userScopes[menuName];
    if (!menuScopeList) {
      return false;
    }

    return menuScopeList.includes(scopeName);
  }, [isAdmin, userScopes]);

  // Get all scopes for a menu
  const getMenuScopes = useCallback((menuName: string): string[] => {
    return userScopes[menuName] || [];
  }, [userScopes]);

  // Check if user has at least one scope for a path
  const hasScopesForPath = useCallback((path: string): boolean => {
    // Super Admin / system role users get all access without scope checks
    if (isAdmin) {
      return true;
    }

    // If no pathToMenuName mapping yet (still loading), allow access temporarily
    if (Object.keys(pathToMenuName).length === 0) {
      return true;
    }

    // Remove query parameters from path for matching
    const basePath = path.split('?')[0];

    // Try exact match first
    let menuName = pathToMenuName[basePath];

    // If no exact match, try to find a parent path match
    // e.g., /parking/check-ins/create should match /parking/check-ins
    if (!menuName) {
      const pathParts = basePath.split('/').filter(Boolean);
      // Try progressively shorter paths
      for (let i = pathParts.length - 1; i >= 1; i--) {
        const parentPath = '/' + pathParts.slice(0, i).join('/');
        if (pathToMenuName[parentPath]) {
          menuName = pathToMenuName[parentPath];
          break;
        }
      }
    }

    // If path not in mapping, it means this screen has no scopes defined in the database
    // Allow access - these are likely "Coming Soon" or unmanaged screens
    if (!menuName) {
      return true;
    }

    // Check if user has at least one scope for this menu
    const scopes = userScopes[menuName];
    return scopes && scopes.length > 0;
  }, [isAdmin, pathToMenuName, userScopes]);

  // Load menus on auth change
  useEffect(() => {
    if (authLoading) {
      // Auth is still verifying token — keep loading=true, don't clear menus yet
      return;
    }
    if (isAuthenticated && user) {
      refreshMenus();
    } else {
      clearMenus();
    }
  }, [authLoading, isAuthenticated, user?.id]); // Only depend on user.id to avoid unnecessary re-fetches

  // Periodically refresh menus to pick up permission changes made by admins.
  // SILENT refresh — must not show a loading spinner or clear access on a
  // transient network error, so users are never bounced out of an open form.
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const MENU_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    const interval = setInterval(() => {
      refreshMenus({ silent: true });
    }, MENU_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAuthenticated, user?.id, refreshMenus]);

  // Memoize context value
  const contextValue = useMemo<MenuContextValue>(() => ({
    userMenus,
    accessiblePaths,
    userScopes,
    pathToMenuId,
    isAdmin,
    loading,
    error,
    hasAccess,
    hasModuleAccess,
    hasScope,
    getMenuScopes,
    hasScopesForPath,
    refreshMenus,
    clearMenus,
  }), [userMenus, accessiblePaths, userScopes, pathToMenuId, isAdmin, loading, error, hasAccess, hasModuleAccess, hasScope, getMenuScopes, hasScopesForPath, refreshMenus, clearMenus]);

  return (
    <MenuContext.Provider value={contextValue}>
      {children}
    </MenuContext.Provider>
  );
}

export function useMenuContext(): MenuContextValue {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenuContext must be used within a MenuProvider');
  }
  return context;
}

// Helper hook for checking route access
export function useHasAccess(path: string): boolean {
  const { hasAccess } = useMenuContext();
  return hasAccess(path);
}

// Helper hook for checking module access
export function useHasModuleAccess(modulePath: string): boolean {
  const { hasModuleAccess } = useMenuContext();
  return hasModuleAccess(modulePath);
}

// Helper hook for checking scope access
export function useHasScope(menuName: string, scopeName: string): boolean {
  const { hasScope } = useMenuContext();
  return hasScope(menuName, scopeName);
}

// Helper hook for getting all scopes for a menu
export function useMenuScopes(menuName: string): string[] {
  const { getMenuScopes } = useMenuContext();
  return getMenuScopes(menuName);
}
